import axios from 'axios';
import useSWR from 'swr';
import { useEffect, useState } from 'react';
import { EmptyStateTrello } from '../EmptyStateTrello';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { LogisticsServicesLoaderCards } from '../LogisticsServicesLoaderCards';
import LogisticsServiceTrelloCard from '../LogisticsServiceTrelloCard/LogisticsServiceTrelloCard';
import {
  IlogisticServiceTrelloCardData,
  LOGISTIC_DROPPABLE_ID,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../types/services';
import useFiltersState from '../../../../state/useFiltersState';
import useDebounce from '../../../../hooks/useDebounce';
import { getLogisticServicesTrelloDataByState } from '../../../../services/services';
import useServiceState from '../../../../state/useLsTrelloState';
import { displayErrorNotification } from '../../../../utils/displayNudosStandardNotifications';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import './LogisticServiceTrelloColumn.scss';

const LogisticServiceTrelloColumn = ({
  status,
  searchText,
  openDelayedModalCallback,
  changeStateFirsLoad
}: {
  status?: string;
  searchText: string;
  openDelayedModalCallback?: (logisticService: IlogisticServiceTrelloCardData) => void;
  changeStateFirsLoad: (state: boolean) => void;
}) => {
  const { logisticsServicesFilters } = useFiltersState();
  const { logisticServices, setLogisticServices, setInitialLogisticServices, initialLogisticServices, updatedCounter } =
    useServiceState();
  const { onlyDevelopers } = useOnlyDevelopers();
  const [generalLoading, setGeneralLoading] = useState<boolean>(false);
  const [makeFirstCharge, setMakeFirstCharge] = useState<boolean>(false);
  const [logisticServicesGeneral, setLogisticServicesGeneral] = useState<IlogisticServiceTrelloCardData[]>();

  const debouncedSearchText = useDebounce(searchText, 1300);
  const { CANCELLED, CONFIRMED, ACTIVE, DELIVERED, INPROCESS, DELAYED, TOCONFIRMED } = LOGISTIC_SERVICE_STATUS_NAME;
  const { CANCELLED_ID, CONFIRMED_ID, ACTIVE_ID, DELIVERED_ID, INPROCESS_ID, DELAYED_ID, TOCONFIRMED_ID } =
    LOGISTIC_DROPPABLE_ID;

  const className = status === CANCELLED ? 'canceledLSsBox' : 'activeLSsBox';
  const orientation = status === CANCELLED ? 'horizontal' : 'vertical';
  const searchTextQueryParam = `${debouncedSearchText ? `search=${debouncedSearchText.trim()}` : ''}`;
  const emptyTrelloIcon = status === DELAYED ? 'like' : 'truck';
  const hasLogisticsServicesFilters = logisticsServicesFilters && logisticsServicesFilters.length;
  const filtersQueryParam = hasLogisticsServicesFilters
    ? `filters=${encodeURIComponent(JSON.stringify(logisticsServicesFilters))}`
    : '';
  const debouncedFilters = useDebounce(filtersQueryParam, 500);
  const onlyDevsQueryParam = `onlyDevs=${!!onlyDevelopers}`;
  const paramsArray = [onlyDevsQueryParam, searchTextQueryParam, debouncedFilters];
  const queryParams = paramsArray.filter(param => param).join('&');
  const swrKey = `/logistic-service/${status}${queryParams ? `?${queryParams}` : ''}`;

  const {
    data,
    isLoading,
    error,
    mutate: updateTrelloDataCallback
  } = useSWR([swrKey, status], getLogisticServicesTrelloDataByState, {
    suspense: false,
    revalidateOnFocus: false //Has been disabled because the needed revalidation scenarios are already handled by properties like:  revalidateIfStale, dedupingInterval
  });

  const initalLoader = () => {
    if (isLoading && !logisticServicesGeneral?.length && !error) setGeneralLoading(true);
    if (!isLoading && (!!logisticServicesGeneral || !!error)) {
      setGeneralLoading(false);
      if (!makeFirstCharge) {
        setMakeFirstCharge(true);
        changeStateFirsLoad(false);
      }
    }
  };

  const initialData = () => {
    if (data) setLogisticServicesGeneral(data);
  };

  const handleSWRError = () => {
    if (error) {
      if (axios.isCancel(error)) {
        console.error('estado:request:', error?.message);
      } else {
        displayErrorNotification();
      }
    }
  };

  const displayLSCardsList = !!logisticServicesGeneral && !!logisticServicesGeneral?.length && !error;

  const getStatusData = () => {
    switch (status) {
      case CANCELLED:
        return {
          columnName: 'cancelled',
          statusTitle: 'Cancelado',
          droppableId: CANCELLED_ID,
          cardStatusColumnClassname: 'canceled',
          logisticsServiceTrelloCardKey: 'canceledCard',
          emptyStateTrelloText: 'No se han cancelado servicios logísticos los últimos 7 días'
        };
      case DELIVERED:
        return {
          columnName: 'delivered',
          statusTitle: 'Entregados',
          droppableId: DELIVERED_ID,
          cardStatusColumnClassname: 'delivered',
          logisticsServiceTrelloCardKey: 'deliveredCard',
          emptyStateTrelloText: 'No se han entregado servicios logísticos este mes'
        };
      case DELAYED:
        return {
          columnName: 'delayed',
          statusTitle: 'Atrasados',
          droppableId: DELAYED_ID,
          cardStatusColumnClassname: 'delayed',
          logisticsServiceTrelloCardKey: 'delayedCard',
          emptyStateTrelloText: '¡Los servicios logísticos están al día!'
        };
      case ACTIVE:
        return {
          columnName: 'actives',
          statusTitle: 'Activos',
          droppableId: ACTIVE_ID,
          cardStatusColumnClassname: 'active',
          logisticsServiceTrelloCardKey: 'activeCard',
          emptyStateTrelloText: 'No hay servicios logísticos activos'
        };
      case TOCONFIRMED:
        return {
          columnName: 'pendings',
          statusTitle: 'Pendiente de confirmación',
          droppableId: TOCONFIRMED_ID,
          cardStatusColumnClassname: 'pending',
          logisticsServiceTrelloCardKey: 'pendingCard',
          emptyStateTrelloText: 'No hay servicios logísticos pendientes'
        };
      case CONFIRMED:
        return {
          columnName: 'confirmed',
          statusTitle: 'Confirmados',
          droppableId: CONFIRMED_ID,
          cardStatusColumnClassname: 'confirmed',
          logisticsServiceTrelloCardKey: 'confirmed',
          emptyStateTrelloText: 'No se han confirmado servicios logísticos'
        };
      case INPROCESS:
        return {
          columnName: 'inProcess',
          statusTitle: 'En proceso',
          droppableId: INPROCESS_ID,
          cardStatusColumnClassname: 'inProcess',
          logisticsServiceTrelloCardKey: 'inProcessCard',
          emptyStateTrelloText: 'No hay servicios logísticos en proceso'
        };
      default:
        return {
          columnName: 'pendings',
          statusTitle: 'Pendiente de confirmación',
          droppableId: TOCONFIRMED_ID,
          cardStatusColumnClassname: 'pending',
          logisticsServiceTrelloCardKey: 'pendingCard',
          emptyStateTrelloText: 'No hay servicios logísticos pendientes'
        };
    }
  };

  const {
    logisticsServiceTrelloCardKey,
    cardStatusColumnClassname,
    emptyStateTrelloText,
    droppableId,
    statusTitle,
    columnName
  } = getStatusData() || {};

  const getCardStatusClassname = (isLate: boolean) => {
    if (status === DELAYED) return isLate ? 'delayed' : 'delayedUpdated';
    return cardStatusColumnClassname;
  };

  const updateGlobalStateFunction = () => {
    setLogisticServices({ ...(logisticServices || {}), [columnName]: logisticServicesGeneral });
    setInitialLogisticServices({ ...(initialLogisticServices || {}), [columnName]: logisticServicesGeneral });
  };

  useEffect(() => {
    handleSWRError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    initialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    initalLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticServicesGeneral, logisticServicesGeneral?.length, error]);

  useEffect(() => {
    const thisColumnCounter = updatedCounter[status?.replaceAll(' ', '_') || ''];
    if (!thisColumnCounter) return;
    updateTrelloDataCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedCounter[status?.replaceAll(' ', '_') || '']]);

  useEffect(() => {
    updateGlobalStateFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticServicesGeneral]);

  const displayLoader = isLoading || generalLoading;

  if (displayLoader)
    return (
      <div className={className}>
        <div className="stateTitle">{`${statusTitle} (${logisticServicesGeneral?.length || 0})`} </div>
        <div className="cardsList">
          <LogisticsServicesLoaderCards numberCards={4} orientation={orientation} />
        </div>
      </div>
    );

  return (
    <div className={className}>
      <div className="stateTitle">{`${statusTitle} (${logisticServicesGeneral?.length || 0})`} </div>
      <Droppable droppableId={droppableId || ''} direction={orientation}>
        {droppableProvided => (
          <div className="cardsList" {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
            {displayLSCardsList &&
              logisticServicesGeneral?.map((logisticService, index) => (
                <Draggable key={logisticService?.id} draggableId={JSON.stringify(logisticService?.id)} index={index}>
                  {draggableProvided => (
                    <div
                      className="containerLogisticCard"
                      {...draggableProvided.draggableProps}
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.dragHandleProps}
                    >
                      <LogisticsServiceTrelloCard
                        key={`${logisticsServiceTrelloCardKey}${logisticService?.id}`}
                        logisticServiceData={logisticService}
                        cardStatusColumnClassname={getCardStatusClassname(!!logisticService?.isLate)}
                        updateTrelloDataCallback={updateTrelloDataCallback}
                        openDelayedModalCallback={() =>
                          openDelayedModalCallback && openDelayedModalCallback(logisticService)
                        }
                      />
                    </div>
                  )}
                </Draggable>
              ))}
            {!displayLSCardsList && <EmptyStateTrello text={emptyStateTrelloText} icon={emptyTrelloIcon} />}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};
export default LogisticServiceTrelloColumn;
